<template>
  <div>
    <SiteBanner :site-address="siteAddress" :site-name="siteName" />
    <section class="px-4">
      <h1 class="supplemental-title mt-4 text-center text-lg">
        {{ supplemental?.title }}
      </h1>
      <FormGenerator
        :key="formGeneratorKey"
        :schema="schema"
        @form-changed="formChangedEventHandler"
        @visibility-changed="updateVisibleComponentsEventHandler"
        @update-component-visibility="updateComponentVisibilityEventHandler"
        @persist-photo="storePhotoEventHandler"
        @remove-photo="removePhotoEventHandler"
        @document-uploaded="documentUploadedEventHandler"
        @document-removed="documentRemovedEventHandler"
      />
      <PrimaryButton
        :key="submitButtonKey"
        action="Submit"
        class="mt-6 w-full rounded-sm bg-blue-500 p-3 text-white hover:opacity-50"
        identifier="supplemental-form-submit"
        type="submit"
        @click.once="submitForm"
      />
    </section>
    <LoadingView :is-loading="isLoading" :message="loadingMessage" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, nextTick, reactive, ref, toRaw, watch } from 'vue'
  import PrimaryButton from '@/components/PrimaryButton.vue'
  import SiteBanner from '@/components/SiteBanner.vue'
  import { RouteLocationNormalized, useRouter } from 'vue-router'
  import {
    Answer,
    checkAnswers,
    errorMessage,
    FormDocument,
    FormGenerator,
    ImageAnswer,
    removePhoto,
    SchemaType,
    scrollToFirstError,
    storePhoto,
    SupplementalFormType as _SupplementalFormType,
    TrolleyCollection,
    updateComponentVisibility,
    updateVisibleComponents,
    validateFormSubmission,
  } from '@verifiedit/form-generator'
  import { submitSupplementalForm } from '@/services/api/supplementalForm'
  import LoadingView from '@/views/LoadingView.vue'
  import InvalidStoreException from '@/exceptions/InvalidStoreException'
  import { removeImage, storeImage } from '@/services/api/image'
  import { resolveDocumentResources } from '@/components/use/form'
  import objectUrls from '@/components/use/objectUrls'
  import { useCustomerStore } from '@/storage/customer'
  import { storeToRefs } from 'pinia'
  import { useSiteStore } from '@/storage/site'
  import {
    SupplementalParent,
    useSupplementalFormStore,
  } from '@/storage/supplementalForms'
  import { useUserStore } from '@/storage/user'
  import dayjs from 'dayjs'
  import { deleteDocument, storeDocument } from '@/services/api/document'

  export default defineComponent({
    name: 'SupplementalForm',

    components: {
      FormGenerator,
      LoadingView,
      PrimaryButton,
      SiteBanner,
    },

    setup() {
      const {
        address: siteAddress,
        name: siteName,
        id: siteId,
        uuid: siteUuid,
        isSiteStoreDefault,
      } = storeToRefs(useSiteStore())
      const {
        id: userId,
        isUserStoreDefault,
        checkedInTime,
        selectedContractorCompanyName,
      } = storeToRefs(useUserStore())
      const { id: customerId, isCustomerStoreDefault } =
        storeToRefs(useCustomerStore())
      const { currentSupplementalForm, currentParent, routeTo, currentMeta } =
        storeToRefs(useSupplementalFormStore())
      const router = useRouter()

      type BaseSupplementalFormType = _SupplementalFormType

      type SupplementalFormType = BaseSupplementalFormType & {
        template: BaseSupplementalFormType['template'] & {
          components?: BaseSupplementalFormType['template']['schema']
          supplementals?: BaseSupplementalFormType[]
        }
      }

      const supplemental = ref<SupplementalFormType | null>(null)
      const schema = ref<(SchemaType & errorMessage)[]>([])
      const submittableComponentNames = ref<string[]>([])
      const answers: Map<string, Answer> = reactive(new Map())
      const formGeneratorKey = ref<number>(0)
      const { releaseObjectUrl, releaseObjectUrls, trackObjectUrl } =
        objectUrls()

      function updateSupplementalForm() {
        supplemental.value = JSON.parse(
          JSON.stringify(toRaw(currentSupplementalForm.value)),
        ) satisfies SupplementalFormType | null

        if (supplemental.value === null) {
          return
        }

        if ('components' in supplemental.value.template) {
          schema.value = supplemental.value.template.components as (SchemaType &
            errorMessage)[]
        } else if ('schema' in supplemental.value.template) {
          schema.value = supplemental.value?.template.schema as (SchemaType &
            errorMessage)[]
        }

        schema.value.forEach((component) => {
          component.errorMessage = ''
        })

        // Reused rather than remounted for each form in the queue, so release the last form's previews.
        releaseObjectUrls()
        void resolveDocumentResources(schema.value)

        formGeneratorKey.value++
      }

      updateSupplementalForm()

      watch(currentSupplementalForm, () => {
        if (currentSupplementalForm.value !== null) {
          updateSupplementalForm()
        }
      })

      const isLoading = ref<boolean>(false)
      const loadingMessage = ref<string>('Loading form...')
      const submitButtonKey = ref<number>(0)

      async function submitForm(): Promise<void> {
        submitButtonKey.value += 1
        const canSubmit = validateFormSubmission(
          schema.value as (SchemaType & errorMessage)[],
          answers,
          submittableComponentNames.value,
        )

        if (!canSubmit) {
          await nextTick()
          scrollToFirstError()
          return
        }

        if (isSiteStoreDefault.value) {
          throw new InvalidStoreException()
        }
        loadingMessage.value = 'Saving form...'
        isLoading.value = true

        const response = await submitSupplementalForm(
          Number(supplemental.value?.identifier),
          Object.fromEntries(answers),
          siteId.value,
          siteUuid.value,
          dayjs(checkedInTime.value).format('DD MMM YYYY [at] HH:mm'),
          dayjs().format('DD MMM YYYY [at] HH:mm'),
          currentParent.value as SupplementalParent,
          currentMeta.value,
          selectedContractorCompanyName.value || undefined,
        )
        if (response.data.data.code === 200) {
          useSupplementalFormStore().removeSupplementalForm(
            currentParent.value as SupplementalParent,
            supplemental.value as SupplementalFormType,
          )
          await router.replace(routeTo.value as RouteLocationNormalized)
        }
        isLoading.value = false
      }

      function formChangedEventHandler(
        changedComponentName: string,
        userInput: string | (string | ImageAnswer | TrolleyCollection)[],
      ) {
        checkAnswers(changedComponentName, userInput, schema.value, answers)
      }

      function updateVisibleComponentsEventHandler(
        components: Map<string, string>,
      ) {
        submittableComponentNames.value = updateVisibleComponents(
          components,
          answers,
        )
      }

      function updateComponentVisibilityEventHandler(
        identifier: string,
        result: { items: string[]; value: boolean },
        key: string,
      ) {
        updateComponentVisibility(identifier, result, key, schema.value)
      }

      async function storePhotoEventHandler(
        imageData: string,
        imageTypeId: number,
        fileName: string | null,
        identifier: string,
      ) {
        if (
          isCustomerStoreDefault.value ||
          isSiteStoreDefault.value ||
          isUserStoreDefault.value
        ) {
          throw new InvalidStoreException()
        }

        loadingMessage.value = 'Saving image...'
        isLoading.value = true

        await storePhoto(
          imageData,
          imageTypeId,
          fileName,
          identifier,
          schema.value,
          () =>
            storeImage(
              customerId.value,
              siteId.value,
              userId.value,
              imageData,
              imageTypeId,
              fileName,
            ),
        )
        isLoading.value = false
      }

      async function removePhotoEventHandler(
        imageId: string,
        identifier: string,
      ) {
        isLoading.value = true
        loadingMessage.value = 'Removing image...'
        await removePhoto(
          imageId,
          identifier,
          schema.value,
          answers,
          removeImage,
        )
        isLoading.value = false
      }

      async function documentUploadedEventHandler(
        identifier: string,
        document: File,
      ) {
        isLoading.value = true
        loadingMessage.value = 'Saving document...'

        const documentComponent = schema.value.find(
          (component) => component.identifier === identifier,
        )

        if (!documentComponent) {
          return
        }

        try {
          let documentType: string | null = null

          if (
            Object.prototype.hasOwnProperty.call(
              documentComponent,
              'documentType',
            )
          ) {
            documentType = documentComponent['documentType'] as string | null
          }

          const response = await storeDocument(
            document,
            document.name,
            document.type,
            documentType,
          )

          // Previewed from the file just picked, not from the url the api answered with: those
          // bytes are in quarantine until the scan clears them, and the browser already holds
          // a copy. The url this makes is released on removal, when the next form in the queue
          // replaces this one, and when the view goes away.
          const uploadedDocument: FormDocument = {
            // What the api detected from the bytes, not what the browser guessed from the name.
            mimeType: response.mimeType || document.type,
            name: document.name,
            preview: trackObjectUrl(URL.createObjectURL(document)),
            status: response.status,
            uuid: response.uuid,
          }

          if (documentComponent.documents) {
            documentComponent.documents.push(uploadedDocument)
          } else {
            documentComponent.documents = [uploadedDocument]
          }
        } catch (error) {
          console.error('Error uploading document:', error)
          throw error
        } finally {
          isLoading.value = false
          loadingMessage.value = 'Loading...'
        }
      }

      async function documentRemovedEventHandler(
        identifier: string,
        documentUuid: string,
      ) {
        isLoading.value = true
        loadingMessage.value = 'Removing document...'

        try {
          const deleted = await deleteDocument(documentUuid)

          if (!deleted) {
            console.error('Error deleting document with uuid:', documentUuid)
            return
          }

          const documentComponent = schema.value.find(
            (component) => component.identifier === identifier,
          )

          if (documentComponent && documentComponent.documents) {
            const removed = documentComponent.documents.find(
              (document) => document.uuid === documentUuid,
            )

            if (removed?.preview) {
              releaseObjectUrl(removed.preview)
            }

            documentComponent.documents = documentComponent.documents.filter(
              (document) => document.uuid !== documentUuid,
            )
          }
        } catch (error) {
          console.error('Error removing document:', error)
          throw error
        } finally {
          isLoading.value = false
          loadingMessage.value = 'Loading...'
        }
      }

      return {
        answers,
        documentRemovedEventHandler,
        documentUploadedEventHandler,
        formChangedEventHandler,
        formGeneratorKey,
        isLoading,
        loadingMessage,
        removePhotoEventHandler,
        schema,
        siteAddress,
        siteName,
        storePhotoEventHandler,
        submitButtonKey,
        submitForm,
        supplemental,
        updateComponentVisibilityEventHandler,
        updateVisibleComponentsEventHandler,
      }
    },
  })
</script>
